import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { api, setSessionToken } from '../lib/api';
import { motion } from 'framer-motion';
import { Sparkles, ShieldCheck, Building2, Receipt, ClipboardCheck, Loader2 } from 'lucide-react';

const FEATURES = [
  { icon: Building2, title: 'Property Pipeline', body: 'Kanban from Sourcing to Sold with live ROI per project.' },
  { icon: Sparkles, title: 'AI Deal Analyzer', body: 'Fair price, ARV, deal score & comparables — powered by Iris AI.' },
  { icon: ShieldCheck, title: 'BOQ Fraud Detection', body: 'Compare vendor BOQs & flag inflated rates, duplicates.' },
  { icon: ClipboardCheck, title: 'Approval Workflow', body: 'Accountant → PM → MD → Payments with full audit trail.' },
  { icon: Receipt, title: 'Vendor Portal', body: 'Vendors log in, submit BOQs & invoices, track payments.' },
];

export default function Login() {
  const { user, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) window.location.href = '/app/dashboard';
  }, [user]);

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) return;
    setSubmitting(true);
    try {
      const { data } = await api.post('/auth/login', { email: email.trim().toLowerCase(), password });
      if (data?.session_token) {
        setSessionToken(data.session_token);
        window.location.href = '/app/dashboard';
        return;
      }
      setError('Login failed. Please try again.');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid email or password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <div className="ambient-bg" />

      {/* Left — hero */}
      <div className="md:w-[58%] p-8 md:p-16 flex flex-col relative">
        <div className="flex items-center gap-3 mb-16">
          <div className="w-14 h-14 rounded-2xl bg-white grid place-items-center shadow-xl overflow-hidden p-2 border border-slate-200">
            <img src="/iris-icon.svg" alt="Iris" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-serif text-2xl leading-none">Iris Realty</div>
            <div className="overline mt-1" style={{ fontSize: '0.62rem' }}>Property Operating System · Dubai · London</div>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="overline mb-4">Built for Iris Realty</div>
          <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight text-slate-900 mb-6">
            Every deal.<br/>
            Every drawing.<br/>
            Every dirham.<br/>
            <span className="italic text-[#679286]">Under one glass roof.</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
            The end-to-end operating system for property investment & development in the UAE and UK — from AI-powered sourcing and BOQ fraud detection to
            vendor payments, DM permits, and buyer handover.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.8 }}
          className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl"
        >
          {FEATURES.map((f, i) => (
            <div key={i} className="glass p-4 lift">
              <f.icon size={20} className="text-[#679286] mb-2" />
              <div className="font-medium text-sm text-slate-800">{f.title}</div>
              <div className="text-xs text-slate-500 mt-1 leading-relaxed">{f.body}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Right — login card */}
      <div className="md:w-[42%] p-8 md:p-12 flex items-center justify-center relative">
        <motion.div
          initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}
          className="glass-strong w-full max-w-md p-10"
        >
          <div className="overline mb-3">Sign in</div>
          <h2 className="font-serif text-3xl mb-2">Welcome back</h2>
          <p className="text-sm text-slate-600 mb-8">Sign in with your Iris Realty admin credentials.</p>

          <form onSubmit={handleLocalLogin} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@irisrealty.com"
                autoComplete="email"
                required
                data-testid="login-email-input"
                className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-white/70 focus:outline-none focus:ring-2 focus:ring-[#679286]/40 focus:border-[#679286] text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                data-testid="login-password-input"
                className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-white/70 focus:outline-none focus:ring-2 focus:ring-[#679286]/40 focus:border-[#679286] text-sm"
              />
            </div>

            {error ? (
              <div data-testid="login-error" className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={submitting || loading}
              data-testid="login-submit-btn"
              className="w-full bg-slate-900 text-white rounded-full py-3 flex items-center justify-center gap-2 hover:bg-slate-800 disabled:opacity-60 transition-all hover:-translate-y-0.5"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
              <span className="font-medium text-sm">Sign in</span>
            </button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200/80" />
            <span className="text-[10px] uppercase tracking-widest text-slate-400">Iris Realty · Private Access</span>
            <div className="h-px flex-1 bg-slate-200/80" />
          </div>

          <p className="text-xs text-slate-500 leading-relaxed">
            This workspace is invite-only. If you need access, please ask your workspace owner to
            create an account for you from Team → Invite user.
          </p>

          <div className="mt-8 pt-6 border-t border-slate-200/70">
            <div className="overline mb-2">Are you a vendor?</div>
            <a href="/vendor-portal" className="text-xs text-[#679286] hover:underline">Go to Vendor Portal →</a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
